import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DirectGoodsFilter,
  DirectGoodsResponse,
} from '../../modules/DirectGoods/types';
import { DirectGoodsReqAPI } from '../../modules/DirectGoods/api';
import { useCustomToast } from '../../components';

// Query key for caching
const keyQuery = ['directGoods'] as const;

export const useFetchDirectGoodsRequests = (
  filters?: Partial<DirectGoodsFilter>
) => {
  return useQuery<DirectGoodsResponse, Error>({
    queryKey: [keyQuery, filters || {}],
    queryFn: ({ queryKey }) => DirectGoodsReqAPI.gets({ queryKey }),
    enabled: true,
  });
};

// Fetch a single goods request by ID
export const useFetchDirectGoodsById = (petition_id?: number) => {
  return useQuery({
    queryKey: [...keyQuery, petition_id],
    queryFn: () =>
      petition_id
        ? DirectGoodsReqAPI.get(petition_id)
        : Promise.resolve(undefined),
    enabled: !!petition_id,
  });
};

// Download files
export const useDownloadFilesDirectGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (petition_id: number) =>
      DirectGoodsReqAPI.downloadFiles(petition_id),
    onSuccess: () => {
      showToast('success', 'Exportación exitosa');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

// Download Pdf
export const useDownloadPDFDirectGoods = () => {
  const { showToast } = useCustomToast();
  return useMutation({
    mutationFn: (petition_id: number) =>
      DirectGoodsReqAPI.downloadPDF(petition_id),
    onSuccess: () => {
      showToast('success', 'Certificado exitoso');
    },
    onError: () => {
      showToast('error', 'Error al exportar');
    },
  });
};

export const useAcceptGoodDelivery = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      petition_id,
    }: {
      body: any;
      petition_id: number;
    }) => DirectGoodsReqAPI.acceptDelivery(petition_id, body),
    onSuccess: () => {
      showToast('success', 'Solicitud aceptada correctamente');
      queryClient.invalidateQueries({ queryKey: [keyQuery] });
    },
  });
};

export const useRejectGoodDelivery = () => {
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      petition_id,
    }: {
      body: any;
      petition_id: number;
    }) => DirectGoodsReqAPI.rejectDelivery(petition_id, body),
    onSuccess: () => {
      showToast('success', 'Solicitud rechazada correctamente');
      queryClient.invalidateQueries({ queryKey: [keyQuery] });
    },
  });
};

export const useGenerateGoodDeliveryToken = () => {
  const { showToast } = useCustomToast();

  return useMutation({
    mutationFn: async (petition_id: number) =>
      DirectGoodsReqAPI.generateDeliveryToken(petition_id),
    onSuccess: () => {
      showToast('success', 'Se ha generado el token de entrega');
    },
  });
};
